import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import Input from '@/components/base/Input';
import { systemSettings } from '@/mocks/settings';
import { changePassword } from '@/services/settingsService';

interface PasswordSectionProps {
  onSuccess: () => void;
}

interface FieldState {
  current: string;
  next: string;
  confirm: string;
}

function PasswordInput({
  id,
  name,
  label,
  value,
  onChange,
  error,
  showLabel,
  hideLabel,
}: {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  showLabel: string;
  hideLabel: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <Input
      id={id}
      name={name}
      type={visible ? 'text' : 'password'}
      label={label}
      value={value}
      maxLength={64}
      autoComplete="off"
      error={error}
      onChange={(e) => onChange(e.target.value)}
      trailing={
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          className="flex h-8 w-8 items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
        >
          <i className={visible ? 'ri-eye-off-line text-lg' : 'ri-eye-line text-lg'} aria-hidden="true" />
        </button>
      }
    />
  );
}

/** Standalone password-change section (independent from the data-change request). */
export default function PasswordSection({ onSuccess }: PasswordSectionProps) {
  const { t } = useTranslation();
  const [fields, setFields] = useState<FieldState>({ current: '', next: '', confirm: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const set = (key: keyof FieldState, value: string) =>
    setFields((prev) => ({ ...prev, [key]: value }));

  const reset = () => {
    setFields({ current: '', next: '', confirm: '' });
    setErrors({});
  };

  const handleSubmit = async () => {
    setFormError(null);
    const next: Record<string, string> = {};
    if (!fields.current || !fields.next || !fields.confirm) {
      next.current = t('settings.password.error.required');
    }
    if (fields.next && fields.next.length < systemSettings.password.minLength) {
      next.next = t('settings.password.error.minLength', { count: systemSettings.password.minLength });
    }
    if (fields.next && fields.confirm && fields.next !== fields.confirm) {
      next.confirm = t('settings.password.error.mismatch');
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const result = await changePassword(fields.current, fields.next);
      if (result.ok) {
        reset();
        onSuccess();
      } else {
        setFormError(t('settings.password.error.wrongCurrent'));
      }
    } catch {
      setFormError(t('settings.password.error.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="flex items-start gap-3 border-b border-background-200 px-4 py-4 md:px-5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
          <i className="ri-lock-password-line text-xl leading-none" aria-hidden="true" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-foreground-950">{t('settings.section.security')}</h2>
          <p className="mt-0.5 text-xs text-foreground-500">{t('settings.password.desc')}</p>
        </div>
      </div>

      <div className="space-y-4 p-4 md:p-5">
        <h3 className="text-sm font-semibold text-foreground-950">{t('settings.password.title')}</h3>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <PasswordInput
            id="pw-current"
            name="currentPassword"
            label={t('settings.password.current')}
            value={fields.current}
            onChange={(v) => set('current', v)}
            error={errors.current}
            showLabel={t('settings.password.show')}
            hideLabel={t('settings.password.hide')}
          />
          <PasswordInput
            id="pw-new"
            name="newPassword"
            label={t('settings.password.new')}
            value={fields.next}
            onChange={(v) => set('next', v)}
            error={errors.next}
            showLabel={t('settings.password.show')}
            hideLabel={t('settings.password.hide')}
          />
          <PasswordInput
            id="pw-confirm"
            name="confirmPassword"
            label={t('settings.password.confirm')}
            value={fields.confirm}
            onChange={(v) => set('confirm', v)}
            error={errors.confirm}
            showLabel={t('settings.password.show')}
            hideLabel={t('settings.password.hide')}
          />
        </div>

        {formError ? (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
          >
            <i className="ri-error-warning-line mt-0.5 text-lg text-rose-600" aria-hidden="true" />
            {formError}
          </div>
        ) : null}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-100 pt-4">
          <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
            <i className="ri-shield-keyhole-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
            {t('settings.password.policyNote')} {t('settings.password.successNote')}
          </p>
          <Button
            variant="primary"
            size="md"
            icon="ri-lock-password-line"
            loading={submitting}
            onClick={handleSubmit}
          >
            {t('settings.password.submit')}
          </Button>
        </div>
      </div>
    </Card>
  );
}